"use client";

import React from "react";
import { X } from "lucide-react";

interface HorizontalRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (color: string, width: string, thickness: string) => void;
  currentColor?: string;
  currentWidth?: string;
  currentThickness?: string;
}

export const HorizontalRuleModal: React.FC<HorizontalRuleModalProps> = ({
  isOpen,
  onClose,
  onInsert,
  currentColor = "#000000",
  currentWidth = "100",
  currentThickness = "0.2",
}) => {
  const [color, setColor] = React.useState(currentColor);
  const [width, setWidth] = React.useState(currentWidth);
  const [thickness, setThickness] = React.useState(currentThickness);

  React.useEffect(() => {
    if (isOpen) {
      setColor(currentColor);
      setWidth(currentWidth);
      setThickness(currentThickness);
    }
  }, [isOpen, currentColor, currentWidth, currentThickness]);

  if (!isOpen) return null;

  const handleInsert = () => {
    onInsert(color, `${width}%`, `${thickness}px`);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Insert Horizontal Rule</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                placeholder="#000000"
              />
            </div>
          </div>

          {/* Width */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Width: {width}%
            </label>
            <input
              type="range"
              min="10"
              max="100"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Thickness */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Thickness: {thickness}px
            </label>
            <input
              type="range"
              min="0.2"
              max="10"
              step="0.2"
              value={thickness}
              onChange={(e) => setThickness(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview
            </label>
            <div className="border border-gray-200 rounded p-4 bg-gray-50">
              <hr
                style={{
                  borderColor: color,
                  borderWidth: `${thickness}px`,
                  width: `${width}%`,
                  margin: "0 auto",
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleInsert}
            className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90"
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
};

